import React, { MouseEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { addSuffix } from '../../common/ordinals';
import { useCountdown } from '../../hooks/countdown/countdown';
import { getDatePartsString } from '../../common/dateUtils';
import { isFocusedState, isNotFocusedState, isPrePlayState, isResultedState } from './HorseCard.functions';
import { EventInfo, FinishPosition, HorseCardState, HorseInfo, PrePlayState } from './HorseCard.types';
import { ReactComponent as TickSelectedIcon } from 'theme/assets/icons/Tick_Selected.svg';
import { ReactComponent as TickUnselectedIcon } from 'theme/assets/icons/Tick_Unselected.svg';
import { ReactComponent as SwapIcon } from 'theme/assets/icons/Swap.svg';
import { ReactComponent as ClockIcon } from 'theme/assets/icons/Clock.svg';
import { usePredictorStore } from '../../store/predictor/predictorStore';
import { useEventStore } from '../../store/event/eventStore';
import { isAusGame } from 'store/options/optionsStore';

interface HorseCardColumn1Props {
  state: HorseCardState;
  horseInfo: HorseInfo;
  replaced?: boolean;
}

export const HorseCardColumn1 = ({ state, horseInfo, replaced }: HorseCardColumn1Props) => {
  const hideStats = state === 'not-focused' && horseInfo.nonRunner;
  const showStats = (isFocusedState(state) || isNotFocusedState(state)) && !hideStats;

  return (
    <div className={'horse-card-details'}>
      <HorseCardInfo replaced={replaced} horseInfo={horseInfo} state={state} />
      {showStats && <HorseCardStats horseInfo={horseInfo} />}
    </div>
  );
};
const silkUrl = process.env.REACT_APP_SILK_URL || 'https://www.imagecache365.com/JockeySilksTransparent/';

interface HorseCardColumn2Props {
  state: HorseCardState;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  odds?: string | number;
  position?: FinishPosition | 'NR';
  updated?: boolean;
  raceStarTime?: number;
  raceNumber?: number;
  win?: boolean;
  nonRunner?: boolean;
}

export const HorseCardColumn2 = ({
  state,
  onClick,
  odds,
  position,
  updated,
  raceStarTime,
  raceNumber,
  win,
  nonRunner,
}: HorseCardColumn2Props) => {
  const { t } = useTranslation();
  const prePlay = isPrePlayState(state);
  const resulted = isResultedState(state);
  let positionText = typeof position === 'number' ? addSuffix(position) : position ?? '-';
  const countryCode = usePredictorStore((s) => s.activePredictor?.countryCode);
  positionText = isAusGame(countryCode) && positionText === 'NR' ? 'scratched' : positionText;

  return (
    <div onClick={prePlay && odds !== 'NR' ? onClick : undefined} className='horse-card-column2'>
      {raceStarTime && raceNumber && <RaceCountDown startTime={raceStarTime} raceNumber={raceNumber} />}
      {prePlay && <PrePlayColumn2 odds={odds} state={state} updated={updated} nonRunner={nonRunner} />}
      {resulted && <div className={`${win ? 'won' : ''} horse-card-column2__position`}>{t(positionText)}</div>}
    </div>
  );
};

export const PrePlayIcon = ({ state, nonRunner }: { state: PrePlayState; nonRunner: boolean | undefined }) => {
  switch (state) {
    case 'selection-made':
      return <SwapIcon />;
    case 'focused':
      const icon = !nonRunner ? <TickSelectedIcon /> : null;
      return icon;
    case 'not-focused':
      return <TickUnselectedIcon />;
  }
};

interface HorseCardInfoProps {
  horseInfo: HorseInfo;
  state: HorseCardState;
  replaced?: boolean;
}

interface OddsProps {
  nonRunner: 'nr' | '';
  odds: string | number | undefined;
}

interface NonRunnerOddsProps {
  nonRunnerOddsClass: '' | 'non-runner-odds';
  t: (key: string) => string;
}

const Odds = ({ nonRunner, odds }: OddsProps) => {
  const countryCode = usePredictorStore((s) => s.activePredictor?.countryCode) ?? undefined;
  const { t } = useTranslation();
  const key = odds === 'NR' && isAusGame(countryCode) ? 'scratched' : odds;
  return <div className={`${nonRunner ? `${nonRunner}` : ''} horse-card-info__odds`}>{t(String(key))}</div>;
};

const NonRunnerOdds = ({ nonRunnerOddsClass, t }: NonRunnerOddsProps) => {
  const countryCode = usePredictorStore((s) => s.activePredictor?.countryCode) ?? undefined;
  const key = isAusGame(countryCode) ? 'scratched' : 'nr';
  return <div className={`horse-card-info__odds ${nonRunnerOddsClass}`}>{t(key)}</div>;
};

export const HorseCardInfo = ({ horseInfo, state, replaced }: HorseCardInfoProps) => {
  const { t } = useTranslation();
  const countryCode = usePredictorStore((s) => s.activePredictor?.countryCode);
  const nonRunner = replaced || horseInfo.nonRunner ? 'nr' : '';
  const showOdds = (!isFocusedState(state) && !isNotFocusedState(state)) || horseInfo.odds === 'NR';
  const nonRunnerOddsClass = state === 'not-focused' && horseInfo.nonRunner ? 'non-runner-odds' : '';
  const jerseyClass = isAusGame(countryCode) ? 'horse-card-info__jersey-icon--au' : '';

  return (
    <div className={'horse-card-info'}>
      <div className={`horse-card-info__jersey-icon ${jerseyClass}`}>
        <img
          src={`${silkUrl}${horseInfo.jerseyIcon}`}
          alt={''}
          onError={({ currentTarget }) => {
            currentTarget.onerror = null; // prevents looping
            currentTarget.style.display = 'none';
          }}
        />
      </div>
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <div className={`horse-card-info__runner `}>
          <span className={`${nonRunner}`}>{horseInfo.number}</span>
          <span className={`${nonRunner} horse-card-info__runner-name`}>{horseInfo.horseName}</span>
          {horseInfo.reserve && <span className={`${nonRunner}`}>({`${t('abbr-reserve')}`})</span>}
          {showOdds && <Odds nonRunner={nonRunner} odds={horseInfo.odds} />}
          {horseInfo.nonRunner && horseInfo.odds !== 'NR' && (
            <NonRunnerOdds nonRunnerOddsClass={nonRunnerOddsClass} t={t} />
          )}
        </div>
        <div className={'horse-card-info__extra'}>
          <span className={'horse-card-info__extra__jockey-name'}>{horseInfo?.jockeyName}</span>
          <span className={'horse-card-info__extra__trainer-name'}>{horseInfo.trainerName}</span>
        </div>
      </div>
    </div>
  );
};

export const HorseCardEvent = ({ eventInfo }: { eventInfo: EventInfo }) => {
  return <div className={'horse-card-event'}>{eventInfo.name}</div>;
};

export const HorseCardStats = ({ horseInfo }: { horseInfo: HorseInfo }) => {
  const { t } = useTranslation();
  return (
    <div className={'horse-card-stats'}>
      {horseInfo.form && (
        <>
          <span className={'horse-card-stats__title'}>{t('form')}&nbsp;</span>
          <span className={'horse-card-stats__value'}>{horseInfo?.form}</span>
        </>
      )}
      {horseInfo?.weight && (
        <>
          <span className={'horse-card-stats__title'}>{t('weight')}&nbsp;</span>
          <span className={'horse-card-stats__value'}>{horseInfo?.weight}</span>
        </>
      )}
      {horseInfo?.age && (
        <>
          <span className={'horse-card-stats__title'}>{t('age')}&nbsp;</span>
          <span className={'horse-card-stats__value'}>{horseInfo?.age}</span>
        </>
      )}
    </div>
  );
};

export const PrePlayColumn2 = ({
  updated,
  odds,
  state,
  nonRunner,
}: {
  updated?: boolean;
  odds?: string | number;
  state: PrePlayState;
  nonRunner?: boolean;
}) => {
  const showOdds = (isFocusedState(state) || isNotFocusedState(state)) && odds !== 'NR';
  const { t } = useTranslation();
  return (
    <>
      {showOdds && <div className='horse-card-column2__odds'>{odds}</div>}
      {updated ? (
        <div className={'horse-card-column2__updated'}>{t('updated')}</div>
      ) : (
        <div className='horse-card-column2__icon'>
          <PrePlayIcon state={state} nonRunner={nonRunner} />
        </div>
      )}
    </>
  );
};

export const RaceCountDown = ({ startTime, raceNumber }: { startTime: number; raceNumber: number }) => {
  const setEventFinished = useEventStore((s) => s.setEventFinished);
  const { t } = useTranslation();
  const callback = useCallback(() => {
    return setTimeout(() => {
      setEventFinished(raceNumber);
    }, 1000);
  }, [raceNumber, setEventFinished]);

  const countdown = useCountdown({ startTime, onComplete: callback });
  const countdownString = getDatePartsString(countdown, t);
  return (
    <div className={'horse-card-column2__countdown'}>
      <ClockIcon className={'horse-card-column2__countdown__clock'} />
      <div className={'horse-card-column2__countdown__text'}>{countdownString}</div>
    </div>
  );
};
